/**
 * ListingDetailPage Component
 *
 * Detailed view of a marketplace listing with version history and install option.
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  TagIcon,
  ClockIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  BoltIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Alert } from '../../../components/ui/Alert';
import { InstallAppModal } from '../components/InstallAppModal';
import { useMarketplaceListing } from '../hooks/useMarketplaceListings';
import { useAuth } from '../../../contexts/AuthContext';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import { MARKETPLACE_CATEGORIES } from '../../../types/marketplace.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Get display label for a category
 */
const getCategoryLabel = (category: string): string => {
  const found = MARKETPLACE_CATEGORIES.find((c) => c.value === category);
  return found?.label || category;
};

/**
 * Format date for display
 */
const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * ListingDetailPage component
 */
export const ListingDetailPage: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const layoutProps = useLayoutProps();
  const [showInstallModal, setShowInstallModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || '';

  const { data: listing, isLoading, error } = useMarketplaceListing(listingId || '');

  const latestVersion = listing?.availableVersions?.[listing.availableVersions.length - 1];

  if (isLoading) {
    return (
      <DashboardLayout {...layoutProps}>
        <div className="text-center py-12">
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading listing...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !listing) {
    return (
      <DashboardLayout {...layoutProps}>
        <Alert variant="error">
          {error ? getErrorMessage(error) : 'Listing not found'}
        </Alert>
        <Button
          variant="secondary"
          onClick={() => navigate('/app/marketplace')}
          className="mt-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Marketplace
        </Button>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/app/marketplace')}
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Marketplace
        </Button>

        {/* Header */}
        <div className="card rounded-lg p-6" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex items-start space-x-4">
              {listing.iconUrl ? (
                <img
                  src={listing.iconUrl}
                  alt={listing.name}
                  className="w-20 h-20 rounded-xl object-cover"
                />
              ) : (
                <div className="w-20 h-20 rounded-xl bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-600 font-bold text-2xl">
                    {listing.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{listing.name}</h1>
                <p style={{ color: 'var(--color-text-secondary)' }}>by {listing.publisherTenantCode}</p>
                <div className="flex items-center space-x-3 mt-2">
                  <Badge variant="secondary">
                    <TagIcon className="w-3 h-3 mr-1" />
                    {getCategoryLabel(listing.category)}
                  </Badge>
                  {latestVersion && (
                    <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{latestVersion.version}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-start md:items-end space-y-2">
              <Button onClick={() => setShowInstallModal(true)}>
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Install Application
              </Button>
              <div className="flex items-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                {listing.installCount.toLocaleString()} installs
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <div className="card rounded-lg p-6" style={{ borderColor: 'var(--color-border)' }}>
              <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Description</h2>
              <p className="whitespace-pre-wrap" style={{ color: 'var(--color-text-secondary)' }}>{listing.description}</p>
            </div>

            {/* Supported Actions */}
            {latestVersion?.actions && latestVersion.actions.length > 0 && (
              <div className="card rounded-lg p-6" style={{ borderColor: 'var(--color-border)' }}>
                <h2 className="text-lg font-semibold mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                  <BoltIcon className="h-5 w-5 mr-2 text-primary-500" />
                  Supported Actions
                </h2>
                <div className="flex flex-wrap gap-2">
                  {latestVersion.actions.map((action) => (
                    <Badge key={action} variant="secondary">
                      {action}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Environment Variables */}
            {latestVersion?.environmentVariables && latestVersion.environmentVariables.length > 0 && (
              <div className="card rounded-lg p-6" style={{ borderColor: 'var(--color-border)' }}>
                <h2 className="text-lg font-semibold mb-4 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                  <KeyIcon className="h-5 w-5 mr-2 text-amber-500" />
                  Environment Variables
                </h2>
                <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
                  These environment variables must be configured after installation.
                </p>
                <div className="space-y-2">
                  {latestVersion.environmentVariables.map((envVar) => (
                    <div
                      key={envVar.key}
                      className="flex items-center p-2 rounded-md font-mono text-sm"
                      style={{ backgroundColor: 'var(--color-bg-sunken)' }}
                    >
                      <code style={{ color: 'var(--color-text-primary)' }}>{envVar.key}</code>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Screenshots */}
            {listing.screenshotUrls && listing.screenshotUrls.length > 0 && (
              <div className="card rounded-lg p-6" style={{ borderColor: 'var(--color-border)' }}>
                <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Screenshots</h2>
                <div className="grid grid-cols-2 gap-4">
                  {listing.screenshotUrls.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`${listing.name} screenshot ${index + 1}`}
                      className="rounded-lg border"
                      style={{ borderColor: 'var(--color-border)' }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Version History */}
            <div className="card rounded-lg p-6" style={{ borderColor: 'var(--color-border)' }}>
              <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Version History</h2>
              <div className="space-y-4">
                {listing.availableVersions?.slice().reverse().map((version, index) => (
                  <div
                    key={version.version}
                    className={`pb-4 ${index < listing.availableVersions.length - 1 ? 'border-b' : ''}`}
                    style={index < listing.availableVersions.length - 1 ? { borderColor: 'var(--color-border)' } : undefined}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        {version.version}
                        {index === 0 && (
                          <Badge variant="success" size="sm" className="ml-2">
                            Latest
                          </Badge>
                        )}
                      </span>
                      <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        <ClockIcon className="w-4 h-4 inline mr-1" />
                        {formatDate(version.publishedAt)}
                      </span>
                    </div>
                    {version.releaseNotes && (
                      <p className="mt-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{version.releaseNotes}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info */}
            <div className="card rounded-lg p-6" style={{ borderColor: 'var(--color-border)' }}>
              <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Information</h2>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Publisher</dt>
                  <dd className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{listing.publisherTenantCode}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Category</dt>
                  <dd className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{getCategoryLabel(listing.category)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Latest Version</dt>
                  <dd className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{latestVersion?.version || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Published</dt>
                  <dd className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{formatDate(listing.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Last Updated</dt>
                  <dd className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{formatDate(listing.updatedAt)}</dd>
                </div>
              </dl>
            </div>

            {/* Links */}
            {(listing.documentationUrl || listing.repositoryUrl) && (
              <div className="card rounded-lg p-6" style={{ borderColor: 'var(--color-border)' }}>
                <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--color-text-primary)' }}>Links</h2>
                <div className="space-y-2">
                  {listing.documentationUrl && (
                    <a
                      href={listing.documentationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-sm text-primary-600 hover:text-primary-500"
                    >
                      <DocumentTextIcon className="w-4 h-4 mr-2" />
                      Documentation
                    </a>
                  )}
                  {listing.repositoryUrl && (
                    <a
                      href={listing.repositoryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-sm text-primary-600 hover:text-primary-500"
                    >
                      <CodeBracketIcon className="w-4 h-4 mr-2" />
                      Source Code
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Install Modal */}
        {showInstallModal && (
          <InstallAppModal
            isOpen={showInstallModal}
            onClose={() => setShowInstallModal(false)}
            listing={listing}
            tenantId={tenantId}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default ListingDetailPage;
